import { create } from 'zustand'
import {
  coreCaptureService,
  type CaptureEntryMethod,
  type CoreCaptureOutcome,
  type CoreTodayData,
} from '../services/coreCaptureService'
import type { NormalizedImage } from '../services/imageProcessingService'
import { coreSignalsService } from '../services/coreSignalsService'
import {
  coreDailySynthesisService,
  type DailySynthesisArtifact,
} from '../services/coreDailySynthesisService'
import { useStore } from './useStore'

type CoreTodayState = CoreTodayData & {
  dailySynthesis: DailySynthesisArtifact | null
  isLoading: boolean
  isCapturing: boolean
  isCaptureOpen: boolean
  resolvingReviewIds: string[]
  error: Error | null
  openCapture: () => void
  closeCapture: () => void
  clearError: () => void
  loadToday: (userId: string) => Promise<void>
  captureText: (
    userId: string,
    text: string,
    entryMethod?: CaptureEntryMethod,
  ) => Promise<CoreCaptureOutcome>
  captureImage: (
    userId: string,
    note: string,
    image: NormalizedImage,
    entryMethod?: CaptureEntryMethod,
  ) => Promise<CoreCaptureOutcome>
  captureLink: (
    userId: string,
    url: string,
    note: string,
    entryMethod?: CaptureEntryMethod,
  ) => Promise<CoreCaptureOutcome>
  resolveReview: (userId: string, reviewId: string, choiceId: string) => Promise<void>
  retryCapture: (userId: string, captureId: string) => Promise<void>
  completeAction: (userId: string, actionId: string) => Promise<void>
  reset: () => void
}

const emptyData: CoreTodayData = {
  captures: [],
  contextRecords: [],
  contextRelations: [],
  actions: [],
  reviewItems: [],
  receipts: [],
}

const loadTodayData = async (userId: string) => {
  const [data, dailySynthesis] = await Promise.all([
    coreCaptureService.loadToday(userId),
    coreDailySynthesisService.loadLatest(userId),
  ])
  return { ...data, dailySynthesis }
}

const toError = (error: unknown, fallback: string) => error instanceof Error ? error : new Error(fallback)

export const useCoreTodayStore = create<CoreTodayState>((set) => ({
  ...emptyData,
  dailySynthesis: null,
  isLoading: false,
  isCapturing: false,
  isCaptureOpen: false,
  resolvingReviewIds: [],
  error: null,

  openCapture: () => set({ isCaptureOpen: true }),
  closeCapture: () => set({ isCaptureOpen: false }),
  clearError: () => set({ error: null }),

  loadToday: async (userId) => {
    set({ isLoading: true, error: null })
    try {
      const data = await loadTodayData(userId)
      set({ ...data, isLoading: false })
    } catch (error) {
      set({ error: toError(error, 'Failed to load Today.'), isLoading: false })
    }
  },

  captureText: async (userId, text, entryMethod = 'keyboard') => {
    set({ isCapturing: true, error: null })
    try {
      const enhancedInterpretation = useStore.getState().user?.preferences.automation?.enhancedCaptureInterpretation ?? true
      const outcome = await coreCaptureService.captureText(userId, text, entryMethod, enhancedInterpretation)
      const data = await loadTodayData(userId)
      set({ ...data, isCapturing: false, isCaptureOpen: false })
      coreSignalsService.queueEvaluation()
      return outcome
    } catch (error) {
      const nextError = toError(error, 'Failed to save the capture.')
      set({ error: nextError, isCapturing: false })
      throw nextError
    }
  },


  captureImage: async (userId, note, image, entryMethod = 'keyboard') => {
    set({ isCapturing: true, error: null })
    try {
      const enhancedInterpretation = useStore.getState().user?.preferences.automation?.enhancedCaptureInterpretation ?? true
      const outcome = await coreCaptureService.captureImage(userId, note, image, entryMethod, enhancedInterpretation)
      const data = await loadTodayData(userId)
      set({ ...data, isCapturing: false, isCaptureOpen: false })
      coreSignalsService.queueEvaluation()
      return outcome
    } catch (error) {
      const nextError = toError(error, 'Failed to save the image capture.')
      set({ error: nextError, isCapturing: false })
      throw nextError
    }
  },
  captureLink: async (userId, url, note, entryMethod = 'keyboard') => {
    set({ isCapturing: true, error: null })
    try {
      const enhancedInterpretation = useStore.getState().user?.preferences.automation?.enhancedCaptureInterpretation ?? true
      const outcome = await coreCaptureService.captureLink(userId, url, note, entryMethod, enhancedInterpretation)
      const data = await loadTodayData(userId)
      set({ ...data, isCapturing: false, isCaptureOpen: false })
      coreSignalsService.queueEvaluation()
      return outcome
    } catch (error) {
      const nextError = toError(error, 'Failed to save the link capture.')
      set({ error: nextError, isCapturing: false })
      throw nextError
    }
  },
  resolveReview: async (userId, reviewId, choiceId) => {
    set((state) => ({
      resolvingReviewIds: [...state.resolvingReviewIds, reviewId],
      error: null,
    }))
    try {
      await coreCaptureService.resolveReview(reviewId, choiceId)
      const data = await loadTodayData(userId)
      set((state) => ({
        ...data,
        resolvingReviewIds: state.resolvingReviewIds.filter((id) => id !== reviewId),
      }))
      coreSignalsService.queueEvaluation()
    } catch (error) {
      set((state) => ({
        error: toError(error, 'Failed to apply the review decision.'),
        resolvingReviewIds: state.resolvingReviewIds.filter((id) => id !== reviewId),
      }))
    }
  },

  retryCapture: async (userId, captureId) => {
    set({ error: null })
    try {
      const enhancedInterpretation = useStore.getState().user?.preferences.automation?.enhancedCaptureInterpretation ?? true
      await coreCaptureService.retryCapture(captureId, enhancedInterpretation)
      const data = await loadTodayData(userId)
      set(data)
      coreSignalsService.queueEvaluation()
    } catch (error) {
      set({ error: toError(error, 'Failed to retry the capture.') })
    }
  },

  completeAction: async (userId, actionId) => {
    set({ error: null })
    try {
      await coreCaptureService.completeAction(actionId)
      const data = await loadTodayData(userId)
      set(data)
      coreSignalsService.queueEvaluation()
    } catch (error) {
      set({ error: toError(error, 'Failed to complete the action.') })
    }
  },

  reset: () => set({
    ...emptyData,
    dailySynthesis: null,
    isLoading: false,
    isCapturing: false,
    isCaptureOpen: false,
    resolvingReviewIds: [],
    error: null,
  }),
}))
